import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CascadingLocationSelector from "@/components/CascadingLocationSelector";
import { AlertCircle, ArrowLeft, Bell, Check, Paperclip, X as XIcon, Image as ImageIcon, File as FileIcon, Calendar, Clock, Plus, Trash2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CommanServices } from '@/services/commanServices';
import ParticipatingEntities from '@/components/ParticipatingEntities';
import { captureReporterGeolocation } from '@/utils/reporterGeolocation';

const EXERCISE_CLASSIFICATIONS = ['Tabletop', 'Partial', 'Full-scale', 'Field', 'Joint'];

const NewIncident = () => {
  const [formData, setFormData] = useState({
    reportType: 'Exercise',
    type_id: '',
    event_id: '',
    trigger_id: '',
    severity: '',
    location: null,
    title: '',
    description: '',
    notify_now: false,
    assigned_teams: [],
    tags: [],
    // Exercise scheduling fields
    planned_windows: [{ start: '', end: '' }],
    // Exercise (Disaster Simulation) fields
    exercise_classification: '',
    participating_entities: [],
    reporter_name: (() => {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        return user?.full_name || user?.name || '';
      } catch (e) {
        return '';
      }
    })(),
    reporter_contact: (() => {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        return user?.email || user?.phone || '';
      } catch (e) {
        return '';
      }
    })()
  });

  const navigate = useNavigate();
  const location = useLocation();

  // Pre-fill form from query parameters (Task 4.18)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const typeId = params.get('type_id');
    const eventId = params.get('event_id');
    const triggerId = params.get('trigger_id');
    const locId = params.get('location');

    if (typeId || eventId || triggerId || locId) {
      setFormData(prev => ({
        ...prev,
        type_id: typeId || prev.type_id,
        event_id: eventId || prev.event_id,
        trigger_id: triggerId || prev.trigger_id,
        location: locId || prev.location
      }));
    }
  }, [location]);

  const [incidentTypes, setIncidentTypes] = useState([]);
  const [events, setEvents] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [appUsers, setAppUsers] = useState([]);
  const [suggestions, setSuggestions] = useState({
    incidentTypes: [],
    events: [],
    locations: [],
    incidentRulesData: [],
    incidentRelationships: {}
  });
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [matchedScenario, setMatchedScenario] = useState(null);
  const [gpsData, setGpsData] = useState(null);
  const gpsDataRef = useRef(null);
  const gpsRequestRef = useRef(null);

  const captureCurrentGps = useCallback(() => {
    if (gpsDataRef.current) return Promise.resolve(gpsDataRef.current);
    if (gpsRequestRef.current) return gpsRequestRef.current;

    const request = captureReporterGeolocation()
      .then((nextGps) => {
        gpsDataRef.current = nextGps;
        setGpsData(nextGps);
        return nextGps;
      })
      .catch(() => null)
      .finally(() => {
        if (gpsRequestRef.current === request) gpsRequestRef.current = null;
      });

    gpsRequestRef.current = request;
    return request;
  }, []);

  // Ask early so permission is settled before the user reaches Submit. The
  // same action remains available inline if the browser blocks or times out.
  useEffect(() => {
    void captureCurrentGps();
  }, [captureCurrentGps]);

  // Using global api variable defined at top of file

  // Fetch incident suggestions for cascading logic (same as Teams & Crews)
  const fetchIncidentSuggestions = async () => {
    try {
      const response = await CommanServices.deparincidentclassificationsrules();
      if (response.status) {
        const data = await response.data;
        setSuggestions({
          incidentTypes: data.unique_types,
          events: data.unique_events,
          locations: data.unique_locations,
          incidentRulesData: data.incidents,
          incidentRelationships: data.relationships
        });
      }
    } catch (error) {
      console.error('Error fetching incident suggestions:', error);
    }
  };

  // Fetch incident types and suggestions on mount
  useEffect(() => {
    fetchIncidentTypes();
    fetchTeams();
    fetchDepartments();
    fetchAppUsers();
    fetchIncidentSuggestions();
  }, []);

  // Fetch events when incident type changes
  useEffect(() => {
    if (formData.type_id) {
      fetchEvents(formData.type_id);
    } else {
      setEvents([]);
      setTriggers([]);
    }
  }, [formData.type_id]);

  // Fetch triggers when event changes
  // Fetch triggers when event changes
  useEffect(() => {
    if (formData.type_id && formData.event_id) {
      fetchTriggers(formData.type_id, formData.event_id);
    } else {
      setTriggers([]);
    }
  }, [formData.type_id, formData.event_id]);
  
  // Exercise Bootstrapping: detect a matching Active scenario by classification + location
  useEffect(() => {
    if (formData.reportType !== 'Exercise' || !formData.type_id) {
      setMatchedScenario(null);
      return;
    }
    const locIds = Array.isArray(formData.location)
      ? formData.location.filter(Boolean)
      : (formData.location ? [formData.location] : []);
    if (locIds.length === 0) { setMatchedScenario(null); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await CommanServices.matchScenario({
          type_id: formData.type_id,
          event_id: formData.event_id === 'none' ? '' : (formData.event_id || ''),
          trigger_id: formData.trigger_id === 'none' ? '' : (formData.trigger_id || ''),
          location: locIds.join(','),
        });
        setMatchedScenario((res?.status && res?.data) ? res.data : null);
      } catch (e) {
        setMatchedScenario(null);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [formData.type_id, formData.event_id, formData.trigger_id, formData.location, formData.reportType]);

  const fetchIncidentTypes = async () => {
    try {
      const response = await CommanServices.incidentClassifications();
      if (response.status) {
        const data = await response.data;
        setIncidentTypes(data);
      }
    } catch (error) {
      console.error('Error fetching incident types:', error);
    }
  };

  const fetchEvents = async (typeId) => {
    try {
      const incidentType = incidentTypes.find(type => type.id === typeId);
      if (incidentType) {
        // Use the incident_type name to get filtered events (same as Teams & Crews)
        const eventNames = getFilteredEvents(incidentType.incident_type);
        const eventList = eventNames.map(eventName => ({
          id: eventName,
          name: eventName
        }));
        setEvents(eventList);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    }
  };

  const fetchTriggers = async (typeId, eventId) => {
    try {
      const incidentType = incidentTypes.find(type => type.id === typeId);
      if (incidentType && eventId) {
        // Use the incident_type name and event to get filtered triggers (same as Teams & Crews)
        const triggerNames = getFilteredTriggerValues(incidentType.incident_type, eventId);
        const triggerList = triggerNames.map(triggerName => ({
          id: triggerName,
          name: triggerName
        }));
        setTriggers(triggerList);
      }
    } catch (error) {
      console.error('Error fetching triggers:', error);
      setTriggers([]);
    }
  };

  // Helper functions for smart cascading (same as Teams & Crews)
  const getFilteredEvents = (incidentType) => {
    if (!incidentType || !suggestions.incidentRelationships[incidentType]) {
      return [];
    }
    return Object.keys(suggestions.incidentRelationships[incidentType]).sort();
  };

  const getFilteredTriggerValues = (incidentType, event) => {
    if (!incidentType || !event || !suggestions.incidentRelationships[incidentType]?.[event]) {
      return [];
    }
    return suggestions.incidentRelationships[incidentType][event].sort();
  };

  const fetchTeams = async () => {
    try {
      const response = await CommanServices.getTeamsData();
      if (response.status) {
        const data = await response.data;
        setTeams(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
      setTeams([]); // Ensure teams is always an array
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await CommanServices.departments();
      if (response.status) {
        const data = await response.data;
        setDepartments(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    }
  };

  const fetchAppUsers = async () => {
    try {
      const response = await CommanServices.getAppUsers();
      if (response.status) {
        const data = await response.data;
        setAppUsers(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching app users:', error);
      setAppUsers([]);
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    const isExercise = formData.reportType === 'Exercise';

    // Exercise validation
    if (isExercise) {
      if (!formData.type_id) {
        setError("Incident Type is required for an Exercise");
        setIsSubmitting(false);
        return;
      }
      const exLocCount = Array.isArray(formData.location) ? formData.location.length : (formData.location ? 1 : 0);
      if (exLocCount === 0) {
        setError("Please select an Incident Location for the Exercise");
        setIsSubmitting(false);
        return;
      }
      if (!formData.exercise_classification) {
        setError("Please select an Exercise Classification");
        setIsSubmitting(false);
        return;
      }
      if (!formData.planned_windows || formData.planned_windows.some(w => !w.start || !w.end)) {
        setError("Please provide complete Start and End date/time for the schedule");
        setIsSubmitting(false);
        return;
      }
    }

    try {
      // Capture begins on page load, but an unavailable desktop location
      // provider must never prevent an emergency report from being submitted.
      const reporterGps = gpsDataRef.current || gpsData;

      const incidentData = new FormData();

      // Basic fields
      incidentData.append("type_id", formData.type_id || "");
      incidentData.append("event_id", formData.event_id === "none" ? "" : formData.event_id || "");
      incidentData.append("trigger_id", formData.trigger_id === "none" ? "" : formData.trigger_id || "");
      incidentData.append("severity", formData.severity === "auto" ? "" : formData.severity || "");
      incidentData.append("title", formData.title || "");
      incidentData.append("description", formData.description || "");
      incidentData.append("notify_now", formData.notify_now);

      incidentData.append("is_planned_event", false);
      incidentData.append("is_exercise", isExercise);

      if (isExercise) {
        incidentData.append("status", "requested");
        incidentData.append("exercise_classification", formData.exercise_classification || "");
        if (matchedScenario?.id) {
          incidentData.append("source_scenario_id", matchedScenario.id);
        }
        incidentData.append("participating_entities", JSON.stringify(formData.participating_entities || []));
        incidentData.append("planned_windows", JSON.stringify(formData.planned_windows));

        // Organizational scope derived from participating entities
        const exerciseDepts = Array.from(new Set((formData.participating_entities || [])
          .map((en) => en.department_unit_id)
          .filter((deptId) => deptId && deptId !== 'undefined')));
        exerciseDepts.forEach((deptId, index) => {
          incidentData.append(`impacted_departments[${index}]`, deptId);
        });
      }

      // Handle either a single cascading location or an array of location ids.
      if (Array.isArray(formData.location)) {
        formData.location.filter(Boolean).forEach((locationId, index) => {
          incidentData.append(`location[${index}]`, locationId);
        });
      } else if (formData.location) {
        const locationValue = typeof formData.location === 'object'
          ? JSON.stringify(formData.location)
          : formData.location;
        incidentData.append("location[0]", locationValue);
      }

      const isUnknownLocationSelected = (() => {
        if (formData.location === 'unknown') return true;
        return Array.isArray(formData.location) && formData.location.includes('unknown');
      })();
      incidentData.append("is_unknown_location", isUnknownLocationSelected);
      if (reporterGps) {
        incidentData.append("gps_data", JSON.stringify(reporterGps));
      }
      incidentData.append("reporter_gps_capture_status", reporterGps ? "captured" : "unavailable");

      // Append reporter identity details
      if (formData.reporter_name) incidentData.append("reporter_name", formData.reporter_name);
      if (formData.reporter_contact) incidentData.append("reporter_contact", formData.reporter_contact);

      // Handle assigned teams
      if (Array.isArray(formData.assigned_teams)) {
        formData.assigned_teams.filter(teamId => teamId && teamId !== 'none').forEach((teamId, index) => {
          incidentData.append(`assigned_teams[${index}]`, teamId);
        });
      }

      // Handle tags
      if (Array.isArray(formData.tags)) {
        formData.tags.forEach((tag, index) => {
          incidentData.append(`tags[${index}]`, tag);
        });
      }

      // Append file attachments
      selectedFiles.forEach((file) => {
        incidentData.append('files', file);
      });

      console.log('Submitting incident FormData...');

      const response = await CommanServices.addincidents(incidentData);
      const newIncident = response?.data?.id ? response.data : response;
      const createdIncidentId = newIncident?.id || newIncident?.incident_id;

      if (response?.status !== false && createdIncidentId) {
        console.log('Incident created successfully:', newIncident);
        const label = isExercise
          ? `Drill ${createdIncidentId} requested successfully!`
          : `Incident ${createdIncidentId} created successfully!`;
        setSuccess(label);
        setIsSubmitting(false);
        navigate(`/incident-details/${createdIncidentId}`);
        return;
      } else {
        console.error('Error creating incident:', response.massage);
        setError(response.massage || 'Failed to create incident');
      }
    } catch (error) {
      console.error('Error submitting incident:', error);
      setError('Network error occurred while creating incident');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddWindow = () => {
    setFormData(prev => ({
      ...prev,
      planned_windows: [...prev.planned_windows, { start: '', end: '' }]
    }));
  };

  const handleRemoveWindow = (index) => {
    setFormData(prev => ({
      ...prev,
      planned_windows: prev.planned_windows.filter((_, i) => i !== index)
    }));
  };

  const handleWindowChange = (index, field, value) => {
    const newWindows = [...formData.planned_windows];
    newWindows[index] = { ...newWindows[index], [field]: value };
    setFormData(prev => ({ ...prev, planned_windows: newWindows }));
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'High': return 'bg-red-100 text-red-800 border-red-300';
      case 'Medium': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'Low': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const selectedIncidentType = incidentTypes.find(type => type.id === formData.type_id);
  const selectedEvent = events.find(event => event.id === formData.event_id);

  return (
    <div className="h-screen bg-gray-50 flex flex-1 overflow-hidden">
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        {/* <div className="bg-gradient-to-r from-gray-900 to-blue-900 border-b border-blue-800 px-6 py-4 flex-shrink-0 shadow-md">
          <div className="flex items-center justify-between h-12">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-white">Create New Incident</h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white h-10 px-4">
                <Bell className="h-4 w-4 mr-2" />
                <span className="hidden lg:inline">Alerts</span>
                <Badge className="ml-2 bg-red-500 text-white border-0 text-xs">-</Badge>
              </Button>
              
              <div className="flex items-center space-x-3 bg-gradient-to-r from-gray-700 to-gray-600 rounded-lg px-3 py-2 h-10">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center ring-1 ring-white/20">
                  <span className="text-white text-sm font-semibold">BA</span>
                </div>
                <div className="hidden md:block">
                  <p className="text-sm font-medium text-white leading-tight">Bryan Adams</p>
                  <p className="text-xs text-gray-300 leading-tight">Emergency Manager</p>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <Card className="max-w-4xl mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                  <span>Exercise Planning</span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Error/Success Messages */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-800">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <Check className="h-5 w-5 text-green-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-green-800">{success}</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Incident Classification */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Incident Type <span className="text-red-500">*</span>
                    </label>
                    <Select
                      value={formData.type_id}
                      onValueChange={(value) => updateFormData('type_id', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select incident type" />
                      </SelectTrigger>
                      <SelectContent>
                        {incidentTypes.map((type) => (
                          <SelectItem key={type.id} value={type.id}>
                            <div className="flex items-center justify-between w-full">
                              <span>{type.incident_type}</span>
                              <Badge className={getSeverityColor(type.alert_level)}>
                                {type.alert_level}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Event
                    </label>
                    <Select
                      value={formData.event_id}
                      onValueChange={(value) => updateFormData('event_id', value)}
                      disabled={!formData.type_id}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select event (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No specific event</SelectItem>
                        {events.map((event) => (
                          <SelectItem key={event.id} value={event.id}>
                            <div className="flex items-center justify-between w-full">
                              <span>{event.name}</span>
                              {event.severity && (
                                <Badge className={getSeverityColor(event.severity)}>
                                  {event.severity}
                                </Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Trigger
                    </label>
                    <Select
                      value={formData.trigger_id}
                      onValueChange={(value) => updateFormData('trigger_id', value)}
                      disabled={!formData.event_id}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select trigger (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No specific trigger</SelectItem>
                        {triggers.map((trigger) => (
                          <SelectItem key={trigger.id} value={trigger.id}>
                            {trigger.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Severity Override */}
                {(selectedIncidentType || selectedEvent) && (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Severity Override
                    </label>
                    <div className="flex items-center space-x-4">
                      <Badge className={getSeverityColor(selectedEvent?.severity || selectedIncidentType?.alert_level)}>
                        Auto: {selectedEvent?.severity || selectedIncidentType?.alert_level}
                      </Badge>
                      <span className="text-gray-500">or override:</span>
                      <Select
                        value={formData.severity}
                        onValueChange={(value) => updateFormData('severity', value)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue placeholder="Override" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Use Auto</SelectItem>
                          <SelectItem value="High">High</SelectItem>
                          <SelectItem value="Medium">Medium</SelectItem>
                          <SelectItem value="Low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Incident Location <span className="text-red-500">*</span>
                  </label>
                  <CascadingLocationSelector
                    value={formData.location}
                    onChange={(value) => updateFormData('location', value)}
                    className="w-full"
                  />
                </div>

                {/* Exercise (Disaster Simulation) Specific Fields */}
                {formData.reportType === 'Exercise' && (
                  <div className="space-y-6 border-b pb-6">
                    {/* Exercise Bootstrapping — matched scenario banner */}
                    {matchedScenario && (
                      <div
                        data-testid="matched-scenario-banner"
                        className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3"
                      >
                        <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-600 text-white">
                          <Check className="h-4 w-4" />
                        </span>
                        <div className="text-sm">
                          <p className="font-semibold text-emerald-800">
                            Matching scenario “{matchedScenario.name}” found
                          </p>
                          <p className="text-emerald-700">
                            Its timeline &amp; injects{matchedScenario.injected_action_count ? ` (${matchedScenario.injected_action_count})` : ''} will be loaded automatically when you create this exercise.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Exercise Classification */}
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Exercise Classification <span className="text-red-500">*</span>
                      </label>
                      <Select
                        value={formData.exercise_classification}
                        onValueChange={(value) => updateFormData('exercise_classification', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select exercise type" />
                        </SelectTrigger>
                        <SelectContent>
                          {EXERCISE_CLASSIFICATIONS.map((cls) => (
                            <SelectItem key={cls} value={cls}>
                              {cls}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Exercise schedule */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-emerald-600" />
                          Schedule (Scheduled Drills) <span className="text-red-500">*</span>
                        </label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleAddWindow}
                          className="text-emerald-600 hover:text-emerald-700 h-8 px-2"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Window
                        </Button>
                      </div>

                      <div className="space-y-3">
                        {formData.planned_windows.map((window, index) => (
                          <div key={index} className="flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-emerald-50/50 p-3 rounded-lg border border-emerald-100">
                            <div className="flex-1 w-full">
                              <label className="text-[10px] uppercase tracking-wider font-bold text-emerald-800 mb-1 block">Start Date & Time</label>
                              <Input
                                type="datetime-local"
                                value={window.start}
                                onChange={(e) => handleWindowChange(index, 'start', e.target.value)}
                                className="bg-white"
                              />
                            </div>
                            <div className="flex-1 w-full">
                              <label className="text-[10px] uppercase tracking-wider font-bold text-emerald-800 mb-1 block">End Date & Time</label>
                              <Input
                                type="datetime-local"
                                value={window.end}
                                onChange={(e) => handleWindowChange(index, 'end', e.target.value)}
                                className="bg-white"
                              />
                            </div>
                            {formData.planned_windows.length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleRemoveWindow(index)}
                                className="mt-5 text-red-500 hover:text-red-700 p-2"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Organizational Scope is now captured per-entity below */}

                    {/* Participating Entities and Roles */}
                    <ParticipatingEntities
                      value={formData.participating_entities}
                      onChange={(entities) => updateFormData('participating_entities', entities)}
                      departments={departments}
                      teams={teams}
                      users={appUsers}
                    />
                  </div>
                )}


                {/* Incident Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Title
                    </label>
                    <Input
                      value={formData.title}
                      onChange={(e) => updateFormData('title', e.target.value)}
                      placeholder="Brief incident title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Assigned Teams
                    </label>
                    <Select
                      value={formData.assigned_teams[0] || ''}
                      onValueChange={(value) => updateFormData('assigned_teams', value ? [value] : [])}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select team (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No team assigned</SelectItem>
                        {Array.isArray(teams) && teams.map((team) => (
                          <SelectItem key={team.id} value={team.id}>
                            {team.team_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Reporter fields are not needed for exercises. */}
                {formData.reportType !== 'Exercise' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Reporter Name
                      </label>
                      <Input
                        value={formData.reporter_name}
                        onChange={(e) => updateFormData('reporter_name', e.target.value)}
                        placeholder="Enter reporter's full name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Reporter Contact
                      </label>
                      <Input
                        value={formData.reporter_contact}
                        onChange={(e) => updateFormData('reporter_contact', e.target.value)}
                        placeholder="Phone number or email"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => updateFormData('description', e.target.value)}
                    placeholder="Detailed description of the incident..."
                    className="w-full p-3 border border-gray-300 rounded-lg resize-none"
                    rows={4}
                  />
                </div>

                {/* File Attachments */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Attachments <span className="text-gray-400 font-normal">(optional)</span>
                  </label>

                  {/* Drop Zone */}
                  <label
                    htmlFor="incident-file-upload"
                    className="flex flex-col items-center justify-center gap-2 w-full border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                  >
                    <Paperclip className="h-7 w-7 text-gray-400" />
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-700">Click to attach files</p>
                      <p className="text-xs text-gray-400 mt-0.5">Images, PDFs, documents — any file type</p>
                    </div>
                    <input
                      id="incident-file-upload"
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        const newFiles = Array.from(e.target.files || []);
                        setSelectedFiles(prev => [...prev, ...newFiles]);
                        // Reset input so the same file can be re-added if removed
                        e.target.value = '';
                      }}
                    />
                  </label>

                  {/* Selected Files List */}
                  {selectedFiles.length > 0 && (
                    <ul className="mt-3 space-y-2">
                      {selectedFiles.map((file, idx) => {
                        const isImage = file.type.startsWith('image/');
                        const sizeLabel = file.size >= 1024 * 1024
                          ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
                          : `${Math.round(file.size / 1024)} KB`;
                        return (
                          <li key={idx} className="flex items-center gap-3 p-2.5 border border-gray-200 rounded-lg bg-gray-50">
                            <div className={`flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 ${
                              isImage ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'
                            }`}>
                              {isImage ? <ImageIcon className="h-4 w-4" /> : <FileIcon className="h-4 w-4" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate" title={file.name}>{file.name}</p>
                              <p className="text-xs text-gray-400">{sizeLabel} &bull; {file.type || 'unknown'}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== idx))}
                              className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                              title="Remove file"
                            >
                              <XIcon className="h-4 w-4" />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-4 pt-4 border-t">
                  {formData.reportType !== 'Exercise' && (
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="notify_now"
                        checked={formData.notify_now}
                        onChange={(e) => updateFormData('notify_now', e.target.checked)}
                        className="rounded"
                      />
                      <label htmlFor="notify_now" className="text-sm">
                        Notify assigned teams immediately
                      </label>
                    </div>
                  )}

                  <div className="flex-1"></div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/")}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>

                  {formData.reportType === 'Exercise' ? (
                    <Button
                      type="button"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={handleSubmit}
                      disabled={
                        isSubmitting ||
                        !formData.type_id ||
                        (Array.isArray(formData.location) ? formData.location.length === 0 : !formData.location) ||
                        !formData.exercise_classification ||
                        !formData.planned_windows ||
                        formData.planned_windows.some(w => !w.start || !w.end)
                      }
                    >
                      {isSubmitting ? 'Submitting...' : 'Request Drill'}
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      className="bg-red-600 hover:bg-red-700"
                      disabled={!formData.type_id || isSubmitting}
                    >
                      {isSubmitting ? 'Creating...' : 'Create Incident'}
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default NewIncident;
